import posthog, { PostHogConfig } from 'posthog-js';
import { createContext, useContext } from 'react';
import { POSTHOG_API_HOST, POSTHOG_UI_HOST } from '../constants/posthog';
import { isAIBeesUser } from '../utils/user';
import {
  PostHogContextData as ContextData,
  PostHogContextProps as ContextProps,
} from './types';

const TRACKING_ACTIVE = process.env.NODE_ENV === 'production';

const context = createContext<ContextData>({} as ContextData);

const { Provider } = context;

export const usePostHogContext = () => useContext(context);

export const PostHogProvider = ({ children, apiKey, config }: ContextProps) => {
  const instance = PostHogService.getInstance(apiKey, config);

  return <Provider value={{ posthog: instance }}>{children}</Provider>;
};

export class PostHogService {
  private static instance: PostHogService;

  private constructor(apiKey?: string, config?: Partial<PostHogConfig>) {
    if (apiKey && TRACKING_ACTIVE) {
      posthog.init(apiKey, {
        api_host: POSTHOG_API_HOST,
        ui_host: POSTHOG_UI_HOST,
        autocapture: true,
        capture_pageview: 'history_change',
        capture_pageleave: true,
        disable_session_recording: false,
        debug: false,
        ...config,
      });
    }
  }

  static getInstance(
    apiKey?: string,
    config?: Partial<PostHogConfig>
  ): PostHogService {
    if (!PostHogService.instance && TRACKING_ACTIVE) {
      PostHogService.instance = new PostHogService(apiKey, config);
    }
    return PostHogService.instance;
  }

  identifyUser(userId?: string, userProperties?: Record<string, any>) {
    if (userId) {
      posthog.identify(userId, userProperties);

      // Disable session recording for AI Bees employees to avoid credit usage
      const userEmail = userProperties?.email;
      if (isAIBeesUser(userEmail)) {
        posthog.sessionRecording?.stopRecording?.();
      }
    }
  }

  resetUser() {
    posthog.reset();
  }

  getUserId() {
    return posthog.get_distinct_id();
  }

  trackEvent(
    eventName: string,
    eventData?: Record<string, any>,
    eventOptions?: any
  ) {
    posthog.capture(eventName, eventData, eventOptions);
  }

  group(
    groupType: string,
    groupKey: string,
    groupProperties?: Record<string, any>
  ) {
    posthog.group(groupType, groupKey, groupProperties);
  }

  alias(alias: string) {
    posthog.alias(alias);
  }
}
